import { useState, useCallback, memo } from "react";
import type { DataSourceStatusType } from "../lib/api";
import "./DatasourceCard.css";

export interface DatasourceCardProps {
  id: string;
  name: string;
  region?: string;
  icon: React.ReactNode;
  status: DataSourceStatusType;
  message: string;
  duration?: number;
  progress?: number;
  errorDetail?: string;
  dataCount?: number;
  data?: Record<string, unknown>;
  isExpanded: boolean;
  onToggleExpand: () => void;
  className?: string;
}

const STATUS_CONFIG: Record<DataSourceStatusType, { icon: string; label: string }> = {
  pending: { icon: "⏳", label: "等待中" },
  running: { icon: "🔄", label: "查询中" },
  success: { icon: "✅", label: "成功" },
  error: { icon: "❌", label: "失败" },
  not_found: { icon: "🔍", label: "无数据" },
};

function DatasourceCard({
  id,
  name,
  region,
  icon,
  status,
  message,
  duration,
  progress,
  errorDetail,
  dataCount,
  data,
  isExpanded,
  onToggleExpand,
  className = "",
}: DatasourceCardProps) {
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  const handleCopyData = useCallback(async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("复制失败:", err);
    }
  }, [data]);

  const handleExportCsv = useCallback(() => {
    if (!data || !Array.isArray(data)) return;
    const headers = Object.keys(data[0] as Record<string, unknown>);
    const csvContent = [
      headers.join(","),
      ...(data as Record<string, unknown>[]).map(row =>
        headers.map(h => `"${String(row[h] || "").replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${id}_data.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [data, id]);

  const renderDataTable = () => {
    if (!data) return null;
    if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0] as Record<string, unknown>);
      return (
        <div className="datasource-card-table-wrapper">
          <table className="datasource-card-table">
            <thead>
              <tr>
                {headers.map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data as Record<string, unknown>[]).slice(0, 10).map((row, i) => (
                <tr key={i}>
                  {headers.map(h => (
                    <td key={h}>{String(row[h] ?? "")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {(data as Record<string, unknown>[]).length > 10 && (
            <p className="datasource-card-table-more">
              ... 还有 {(data as Record<string, unknown>[]).length - 10} 条记录
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderKeyMetrics = () => {
    if (!data || Array.isArray(data)) return null;
    const entries = Object.entries(data).filter(([_, v]) =>
      typeof v === "number" || typeof v === "boolean"
    );
    if (entries.length === 0) return null;

    return (
      <div className="datasource-card-metrics">
        {entries.slice(0, 8).map(([key, value]) => (
          <span key={key} className="datasource-card-badge">
            <span className="datasource-card-badge-label">{key}:</span>
            <span className="datasource-card-badge-value">{String(value)}</span>
          </span>
        ))}
      </div>
    );
  };

  return (
    <article
      className={`datasource-card datasource-card-${status} ${className}`}
      aria-label={`${name} - ${statusConfig.label}`}
      role="region"
    >
      <div className="datasource-card-header" onClick={onToggleExpand}>
        <div className="datasource-card-title-section">
          <span className="datasource-card-icon" aria-hidden="true">{icon}</span>
          <div className="datasource-card-title-text">
            <h3 className="datasource-card-name">{name}</h3>
            {region && <span className="datasource-card-region">{region}</span>}
          </div>
        </div>

        <div className="datasource-card-status-indicator">
          <span className={`datasource-card-status-icon ${status}`} aria-hidden="true">
            {statusConfig.icon}
          </span>
          {status === "running" && progress !== undefined && (
            <span className="datasource-card-progress-text">{progress}%</span>
          )}
        </div>
      </div>

      <div className="datasource-card-body">
        <p className="datasource-card-message">{message}</p>

        {duration !== undefined && status !== "pending" && status !== "running" && (
          <span className="datasource-card-duration">({duration.toFixed(1)}s)</span>
        )}

        {dataCount !== undefined && status === "success" && (
          <p className="datasource-card-count">返回 {dataCount} 条记录</p>
        )}

        {status === "error" && errorDetail && (
          <p className="datasource-card-error-detail">{errorDetail}</p>
        )}

        {status === "running" && progress !== undefined && (
          <div className="datasource-card-progress-bar">
            <div
              className="datasource-card-progress-fill"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}
      </div>

      <button
        className={`datasource-card-expand-btn ${isExpanded ? "expanded" : ""}`}
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? "折叠详情" : "展开详情"}
      >
        <span>{isExpanded ? "收起 ▲" : "展开 ▼"}</span>
      </button>

      {isExpanded && (
        <div className="datasource-card-details" role="region" aria-label="详细信息">
          {renderKeyMetrics()}
          {renderDataTable()}

          {data && !Array.isArray(data) && Object.keys(data).length > 0 && (
            <div className="datasource-card-json-section">
              <button
                className="datasource-card-json-toggle"
                onClick={() => setShowJson(!showJson)}
              >
                {showJson ? "隐藏原始数据" : "查看原始 JSON"}
              </button>
              {showJson && (
                <pre className="datasource-card-json-pre">
                  <code>{JSON.stringify(data, null, 2)}</code>
                </pre>
              )}
            </div>
          )}

          <div className="datasource-card-actions">
            {data && (
              <>
                <button
                  className="datasource-card-action-btn"
                  onClick={handleCopyData}
                  aria-label="复制数据到剪贴板"
                >
                  📋 {copied ? "已复制!" : "复制数据"}
                </button>
                {Array.isArray(data) && data.length > 0 && (
                  <button
                    className="datasource-card-action-btn"
                    onClick={handleExportCsv}
                    aria-label="导出为CSV"
                  >
                    📥 导出 CSV
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default memo(DatasourceCard);
